import { useEffect, useRef, useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { speakDevice, stopDevice } from "@/lib/deviceVoice";

function Option({ text, i, picked, answer, onPick }) {
  const isAns = i === answer;
  const cls = picked === null ? "bg-white border-gray-200 hover:border-amber-300" : isAns ? "bg-emerald-50 border-emerald-400" : picked === i ? "bg-amber-50 border-amber-300" : "bg-white border-gray-100 opacity-50";
  return (
    <button onClick={() => onPick(i)} disabled={picked !== null} data-testid={`morale-option-${i}`}
      className={`w-full rounded-3xl border-4 px-6 py-5 font-serif-display italic text-2xl sm:text-3xl text-gray-800 flex items-center justify-between gap-3 text-left transition-colors ${cls}`}>
      <span>… {text}</span>
      {picked !== null && isAns && <Check className="w-8 h-8 text-emerald-600 shrink-0" />}
      {picked === i && !isAns && <X className="w-8 h-8 text-amber-600 shrink-0" />}
    </button>
  );
}

// « Complète la morale » : le début de la morale, la vraie fin parmi 3.
export default function MoraleQuiz({ onComplete }) {
  const [rounds, setRounds] = useState(null);
  const [n, setN] = useState(0);
  const [picked, setPicked] = useState(null);
  const good = useRef(0);
  const done = useRef(false);
  const started = useRef(Date.now());
  useEffect(() => {
    let ignore = false;
    api.get("/fables/morale-quiz", { params: { n: 5 } }).then((r) => !ignore && setRounds(r.data)).catch(() => !ignore && setRounds([]));
    return () => { ignore = true; };
  }, []);
  const r = rounds?.[n];
  useEffect(() => { setPicked(null); if (r) speakDevice(`${r.start}…`); return () => stopDevice(); }, [r]);
  if (!rounds) return <Loader2 className="w-10 h-10 animate-spin text-emerald-700" />;
  if (!r) return <p className="font-patient text-2xl text-gray-500" data-testid="morale-empty">Pas encore de fable publiée pour ce jeu.</p>;
  const pick = (i) => { setPicked(i); if (i === r.answer) good.current += 1; speakDevice(r.morale); };
  const next = () => {
    if (n + 1 < rounds.length) return setN(n + 1);
    if (done.current) return;
    done.current = true;
    const score = Math.round((good.current / rounds.length) * 100);
    onComplete({ score, accuracy: score, duration: Math.round((Date.now() - started.current) / 1000) });
  };
  return (
    <div className="w-full max-w-3xl soft-in text-center" data-testid="morale-quiz">
      <p className="font-patient text-xl text-gray-500 mb-2" data-testid="morale-progress">Fable {n + 1} sur {rounds.length} · « {r.fable} »</p>
      <h2 className="font-patient font-black text-3xl text-gray-800 mb-4">Complétez la morale</h2>
      <p className="mx-auto rounded-3xl bg-amber-50 border-4 border-amber-200 p-6 font-serif-display italic text-3xl sm:text-4xl text-amber-950 mb-6 whitespace-pre-line" data-testid="morale-start">{r.start} …</p>
      <div className="space-y-4">{r.options.map((t, i) => <Option key={t} text={t} i={i} picked={picked} answer={r.answer} onPick={pick} />)}</div>
      {picked !== null && (
        <div className="mt-8" data-testid="morale-feedback">
          <p className="font-patient text-2xl text-gray-700 mb-4">{picked === r.answer ? "Bravo !" : "La vraie morale :"} <em className="font-serif-display">{r.morale}</em></p>
          <button onClick={next} data-testid="morale-next" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-patient font-black text-2xl px-10 h-16 transition-colors">
            {n + 1 < rounds.length ? "Fable suivante" : "Terminer"}
          </button>
        </div>
      )}
    </div>
  );
}
